import { useState, type KeyboardEvent } from 'react';
import { api, errorText, type SiteSummary } from '../api.ts';
import { Dot, Empty, ErrorBox, Loading, Pill, SourceBadge, StatusPill } from '../components/ui.tsx';
import { useLoad, useNow } from '../hooks.ts';
import { href, navigate } from '../router.ts';
import { useRefreshOn } from '../stream.tsx';
import { fmtTime, tzAbbr } from '../time.ts';
import { MasterNav, ReadOnlyBanner } from './MasterNav.tsx';

function SiteRow({ s, now }: { s: SiteSummary; now: number }) {
  const open = () => navigate(href.site(s.id));
  const onKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter') open();
  };
  const hoursTitle =
    s.inContractedHoursNow === null ? 'Contracted hours unknown' : s.inContractedHoursNow ? 'Inside contracted monitoring hours now' : 'Outside contracted monitoring hours now';
  return (
    <tr className={`click${s.active ? '' : ' inactive'}`} onClick={open} onKeyDown={onKey}>
      <td>
        <a href={href.site(s.id)} className="strong" onClick={(e) => e.stopPropagation()}>
          {s.name}
        </a>
        <div className="note mono">{s.siteCode ?? s.id}</div>
      </td>
      <td className="ell">{s.companyName}</td>
      <td>
        <StatusPill status={s.status} />
        {s.alarmsPaused && (
          <span className="chip paused" title={`Alarms paused in the ERP since ${s.alarmsPaused.since}${s.alarmsPaused.note ? ` — ${s.alarmsPaused.note}` : ''}`}>
            {s.alarmsPausedNow ? 'alarms paused' : 'pause ended'}
          </span>
        )}
        {s.held && (
          <Pill tone="warn" title="A change to this site is waiting for a supervisor">
            held
          </Pill>
        )}
        {!s.active && <Pill>deleted</Pill>}
      </td>
      <td className="nowrap">
        <span className="mono">{s.timeZone}</span>
        <div className="note mono">
          {fmtTime(now, s.timeZone, false)} {tzAbbr(s.timeZone, now)}
        </div>
      </td>
      <td className="center">
        <Dot on={s.inContractedHoursNow} title={hoursTitle} />
      </td>
      <td className="mono num">{s.cameraCount}</td>
      <td className="mono num">{s.deviceCount}</td>
      <td>
        <SourceBadge source={s.source} />
      </td>
      <td className="mono num">v{s.version}</td>
    </tr>
  );
}

export function SitesScreen({ companyId }: { companyId: string | null }) {
  const companies = useLoad((signal) => api.companies(signal), []);
  const load = useLoad((signal) => api.sites(companyId ?? undefined, signal), [companyId]);
  useRefreshOn(['replica'], load.reload, 800);
  const now = useNow(30_000);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const rows = (load.data ?? [])
    .filter((s) => !needle || `${s.name} ${s.siteCode ?? ''} ${s.companyName} ${s.address?.city ?? ''}`.toLowerCase().includes(needle))
    .sort((a, b) => a.companyName.localeCompare(b.companyName) || a.name.localeCompare(b.name));
  const companyList = (companies.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="page">
      <div className="page-h">
        <div>
          <h2>Sites</h2>
          <p>Monitored sites with their time zone, contracted hours, and equipment counts.</p>
        </div>
        <span className="sp" />
        <MasterNav current="sites" />
      </div>
      <ReadOnlyBanner />
      <div className="filters">
        <label className="field inline">
          <span className="lbl">Company</span>
          <select value={companyId ?? ''} onChange={(e) => navigate(href.sites(e.target.value || null))} aria-label="Filter by company">
            <option value="">All companies</option>
            {companyId && !companyList.some((c) => c.id === companyId) && <option value={companyId}>{companyId}</option>}
            {companyList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field inline grow">
          <span className="lbl">Search</span>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, code, company, city" />
        </label>
        {companies.error ? <span className="note warn-t" title={errorText(companies.error)}>Company list unavailable</span> : null}
      </div>
      <div className="card2 flush">
        {load.error && !load.data ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load sites" />
        ) : !load.data ? (
          <Loading label="Loading sites…" />
        ) : rows.length === 0 ? (
          <Empty>{load.data.length === 0 ? (companyId ? 'This company has no sites.' : 'No sites yet. Load an import file on the Imports screen.') : 'No sites match the search.'}</Empty>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th scope="col">Site</th>
                  <th scope="col">Company</th>
                  <th scope="col">Status</th>
                  <th scope="col">Time zone</th>
                  <th scope="col" className="center" title="Inside contracted hours now">
                    In hours
                  </th>
                  <th scope="col" className="num">Cameras</th>
                  <th scope="col" className="num">Devices</th>
                  <th scope="col">Source</th>
                  <th scope="col" className="num">Version</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <SiteRow key={s.id} s={s} now={now} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
